// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import React from 'react';
import Notification from '../Notification';
import mergeCustomQuestions from '../mergeCustomQuestions';
import FieldsTable from './FieldsTable';
import _expandColumnsMapping from './_expandColumnsMapping';
import getBottlebooksFieldsMap from './getBottlebooksFieldsMap';
import { z } from 'zod';
const EMPTY = '(empty)';

/**
 * Renders the page where fields are mapped
 * All columns from import are rendered and Bottlebooks fields are available in the dropdown
 * @param {Object} props
 * @param {boolean} props.loading - true if data is loading
 * @param {Object[]} [props.latestBottlebooksFields] - the previous fields mapped to be used on new imports
 * @param {import('../Notification').Notification} [props.notification]
 * @param {function} props.cancelNotification
 * @param {object} props.bottlebooksFieldsMap
 * @param {object[]} props.bottlebooksFieldsSource
 * @param {{name:string, sampleRows: string[]}[]} props.sourceFields
 * @param {function} props.handleFieldsTableChange
 */
export default function MapFieldsToBottlebooks({
  loading,
  notification,
  cancelNotification,
  bottlebooksFieldsMap,
  sourceFields,
  handleFieldsTableChange,
  bottlebooksFieldsSource,
  ...rest
}) {
  if (loading) return null;

  return (
    <React.Fragment>
      <FieldsTable
        bottlebooksFieldsMap={bottlebooksFieldsMap}
        bottlebooksFieldsSource={bottlebooksFieldsSource}
        sourceFields={sourceFields}
        onChange={(value, sourceField) =>
          handleFieldsTableChange(value, sourceField, bottlebooksFieldsMap)
        }
        {...rest}
      />

      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

/**
 *
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} props.importId
 * @param {object[]} props.bottlebooksFieldsSource
 * @param {"product" | "profile"} [props.objectType]
 * @returns
 */
export function useMapFieldsToBottlebooks({
  workspaceId,
  importId,
  bottlebooksFieldsSource,
  objectType,
}) {
  // Ensure objectType is valid
  z.enum(['product', 'profile']).parse(objectType);
  const { loading, /* error, */ data /*, refetch */ } = useQuery(QUERY, {
    variables: {
      workspaceId,
      importId,
    },
  });

  const afterQueryData = afterQuery({
    data,
    bottlebooksFieldsSource,
    objectType,
  });

  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  /** @type {[boolean, (arg0: boolean)=>void]} */
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [_updateDataImportBottlebooksFields] = useMutation(
    updateDataImportBottlebooksFields
  );

  const [bottlebooksFieldsMap, setBottlebooksFieldsMap] = React.useState(
    afterQueryData.originalBottlebooksFieldsMap
  );
  async function updateColumnsMapping() {
    if (isSubmitting) return;
    const expandedBottlebooksFields = _expandColumnsMapping(
      bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap
    );

    try {
      setIsSubmitting(true);
      const { data } = await _updateDataImportBottlebooksFields({
        variables: {
          input: {
            importId,
            bottlebooksFields: expandedBottlebooksFields,
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsSubmitting(false);
      return { error: err };
    }
  }

  function areAllRequiredFieldsFilled(bottlebooksFieldsMap) {
    if (!bottlebooksFieldsMap) return null;
    const anyRequiredMissing = bottlebooksFieldsSource.some(
      ({ key, required }) => {
        if (!required) return false;
        const requiredIsMapped = Object.values(bottlebooksFieldsMap).some(
          ({ bottlebooksFieldName }) => {
            return bottlebooksFieldName === key;
          }
        );
        return !requiredIsMapped;
      }
    );
    return !anyRequiredMissing;
  }

  async function handleFieldsTableChange(
    value,
    sourceField,
    originalBottlebooksFieldsMap
  ) {
    let newMapping = {};
    const bottlebooksFieldsMapSrc = bottlebooksFieldsMap
      ? bottlebooksFieldsMap
      : originalBottlebooksFieldsMap;
    if (value === undefined || value === EMPTY) {
      // newMapping = omit(bottlebooksFieldsMapSrc, sourceField.name);
      newMapping = {
        ...bottlebooksFieldsMapSrc,
        [sourceField.name]: {
          importFieldName: sourceField.name,
          bottlebooksFieldName: '',
        },
      };
      setBottlebooksFieldsMap(newMapping);
    } else {
      newMapping = {
        ...bottlebooksFieldsMapSrc,
        [sourceField.name]: {
          importFieldName: sourceField.name,
          bottlebooksFieldName: value,
        },
      };
      setBottlebooksFieldsMap(newMapping);
    }
  }

  return {
    loading,
    importId,
    ...afterQueryData,
    updateColumnsMapping,
    isValid:
      areAllRequiredFieldsFilled(
        bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap
      ) && !isSubmitting,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    bottlebooksFieldsMap:
      bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap,
    handleFieldsTableChange,
  };
}

/**
 *
 * @param {object} props
 * @param {object} props.data
 * @param {object[]} props.bottlebooksFieldsSource
 * @param {"product" | "profile"} [props.objectType]
 * @returns
 */
function afterQuery({ data, bottlebooksFieldsSource, objectType }) {
  if (!data) return {};

  // TODO we don't need all the imports. Add filter and limit latest 10 or 20
  const bottlebooksFieldsNodes = data?.workspace?.dataImports?.nodes?.filter(
    ({ bottlebooksFields }) => bottlebooksFields
  );
  const usedFieldNames = [];
  const latestBottlebooksFields = [];
  bottlebooksFieldsNodes?.forEach(({ bottlebooksFields }) => {
    bottlebooksFields.forEach((f) => {
      if (!usedFieldNames.includes(f.importFieldName)) {
        latestBottlebooksFields.push(f);
        usedFieldNames.push(f.importFieldName);
      }
    });
  });

  const dataImport = data?.workspace?.dataImport;

  const { source, bottlebooksFields } = dataImport;
  const sourceFieldNames = source?.fields?.reduce((acc, { name }) => {
    acc[name] = 1;
    return acc;
  }, {});
  const filteredLatestBottlebooksFields = latestBottlebooksFields?.filter(
    ({ importFieldName, bottlebooksFieldName }) => {
      if (bottlebooksFieldName && sourceFieldNames[importFieldName] === 1) {
        sourceFieldNames[importFieldName]++;
        return true;
      }
    }
  );

  //The columns automatically matched or matched by the user
  const originalBottlebooksFieldsMap = getBottlebooksFieldsMap({
    bottlebooksFields,
    latestBottlebooksFields: filteredLatestBottlebooksFields,
    sourceFields: source?.fields,
    bottlebooksFieldsSource,
  });

  //Add custom fields into bottlebooksFieldsSource
  const customFields =
    objectType === 'product'
      ? data?.workspace?.configuration?.customProductFields
      : data?.workspace?.configuration?.customProfileFields;
  const mergedBottlebooksFieldsSource = mergeCustomQuestions(
    bottlebooksFieldsSource,
    customFields,
    objectType
  );

  //Source fields excludig importFieldNames that are mapped in identifier fields
  const sourceFields = source?.fields.reduce((acc, { name, rawTexts }) => {
    const isIdentifierField =
      data?.workspace?.dataImport?.identifierFields?.some(
        ({ importFieldName }) => importFieldName === name
      );
    if (!isIdentifierField) {
      acc.push({ name, sampleRows: rawTexts });
    }
    return acc;
  }, []);

  return {
    bottlebooksFieldsSource: mergedBottlebooksFieldsSource,
    latestBottlebooksFields,
    originalBottlebooksFieldsMap,
    sourceFieldNames,
    sourceFields,
  };
}

const QUERY = gql`
  query MapFieldsPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      configuration {
        workspaceId
        customProductFields {
          label
          key
          type
        }
        customProfileFields {
          label
          key
          type
        }
      }
      dataImport(importId: $importId) {
        importId
        bottlebooksFields {
          bottlebooksFieldName
          importFieldName
        }
        source {
          fields {
            name
            bottlebooksFieldName
            rawTexts(first: 3)
          }
        }
        identifierFields {
          bottlebooksFieldName
          importFieldName
        }
      }
      dataImports(
        # We only want to import mappings from the same type of imports
        filter: {
          type: {
            in: [
              "productDataImport"
              "productCreationImport"
              "productAllInOneImport"
            ]
          }
        }
      ) {
        nodes {
          importId
          bottlebooksFields {
            bottlebooksFieldName
            importFieldName
          }
        }
      }
    }
  }
`;

export const updateDataImportBottlebooksFields = gql`
  mutation updateDataImportBottlebooksFields(
    $input: UpdateDataImportBottlebooksFieldsInput!
  ) {
    result: updateDataImportBottlebooksFields(input: $input) {
      code
      success
      message
      dataImport {
        importId
        bottlebooksFields {
          bottlebooksFieldName
          importFieldName
        }
      }
    }
  }
`;
